import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Form,
  TextArea,
  Segment,
  Grid,
  Icon,
  Dimmer,
  Loader,
  Message,
} from 'semantic-ui-react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import './App.css';

import server_config from './serverconfig';
const node_address = server_config["node_address"];
const MAX_TEXT_SIZE_MB = 5;
const MAX_TEXT_SIZE = MAX_TEXT_SIZE_MB * 1024 * 1024; //5 MB
const MAX_DOCUMENT_SIZE_MB = 20;
const MAX_DOCUMENT_SIZE = MAX_DOCUMENT_SIZE_MB * 1024 * 1024; //20 MB
const MAX_IMAGE_SIZE_MB = 30;
const MAX_IMAGE_SIZE = MAX_IMAGE_SIZE_MB * 1024 * 1024; //30 MB

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const compressAndGetBase64 = (file, maxWidth = 1600, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);

      URL.revokeObjectURL(objectUrl);
      resolve(compressedBase64);
    };

    img.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };

    img.src = objectUrl;
  });
};

const fileToText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file); // <--- Extracts raw text
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const loadPdfJs = () => {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
      return;
    }
    const script = document.createElement('script');
    script.src = '/pdf.min.js';

    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };

    script.onerror = () => reject(new Error("Failed to load PDF.js"));
    document.body.appendChild(script);
  });
};


//Helper for PDF Documents (.pdf)
const pdfToText = async (file) => {
  try {
    const pdfjsLib = await loadPdfJs();

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = '';

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += `\n--- Page ${pageNum} ---\n${pageText}\n`;
    }

    return fullText;
  } catch (error) {
    console.error("PDF Extraction Error:", error);
    throw error;
  }
};

//Helper for Word Documents (.docx)
const docxToText = async (file) => {
  const mammoth = await import('mammoth'); //DYNAMIC IMPORT: Only downloads Mammoth if a Word file is dropped
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
};

//Helper for Excel Spreadsheets (.xlsx)
const xlsxToText = async (file) => {//DYNAMIC IMPORT: Only downloads SheetJS if an Excel file is dropped
  const XLSX = await import('xlsx');
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  let fullText = '';
  workbook.SheetNames.forEach(sheetName => {
    const worksheet = workbook.Sheets[sheetName];
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    fullText += `\n--- Sheet: ${sheetName} ---\n${csv}\n`;
  });
  return fullText;
};

//Helper to extract ZIP files and turn them into standard File objects
const extractZipFiles = async (zipFile) => {
  const JSZip = (await import('jszip')).default; //Lazy load JSZip only when needed
  const zip = new JSZip();
  const loadedZip = await zip.loadAsync(zipFile);

  const extractedFiles = [];

  //Loop through everything inside the zip
  for (const [relativePath, zipEntry] of Object.entries(loadedZip.files)) {
    const lowerName = zipEntry.name.toLowerCase().split('/').pop();

    //Skip folders, Mac system files, and hidden files
    if (zipEntry.dir || relativePath.includes('__MACOSX') || lowerName.startsWith('.')) {
      continue;
    }

    //Skip compiled binaries, audio, video, and executables
    const junkExtensions = ['.exe', '.dll', '.so', '.dmg', '.iso', '.mp3', '.mp4', '.zip', '.tar', '.gz'];
    if (junkExtensions.some(ext => lowerName.endsWith(ext))) {
      console.log(`Skipping unsupported file inside zip: ${zipEntry.name}`);
      continue;
    }

    //Convert the zip entry into a binary Blob
    const blob = await zipEntry.async('blob');

    //Assign a basic mime type to help your downstream router
    let mimeType = '';
    if (lowerName.match(/\.(jpeg|jpg)$/)) mimeType = 'image/jpeg';
    else if (lowerName.endsWith('.png')) mimeType = 'image/png';
    else if (lowerName.endsWith('.webp')) mimeType = 'image/webp';
    else if (lowerName.endsWith('.pdf')) mimeType = 'application/pdf';
    else if (lowerName.endsWith('.csv') || lowerName.endsWith('.txt') || lowerName.endsWith('.md') || lowerName.endsWith('readme')) mimeType = 'text/plain';

    const file = new File([blob], relativePath, { type: mimeType });
    extractedFiles.push(file);
  }

  return extractedFiles;
};

//File downloader
const downloadFile = (filename, content, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};


const setCookie = (name, value, days) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=Strict";
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') { c = c.substring(1, c.length); } //.trim() could also work
    if (c.indexOf(nameEQ) === 0) { return c.substring(nameEQ.length, c.length); }
  }
  return null;
};

const eraseCookie = (name) => {
  document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

const requestWakeLock = async () => {
  try {
    if ('wakeLock' in navigator) {
      const wakeLock = await navigator.wakeLock.request('screen');
      return wakeLock;
    }
  } catch (err) {
    console.log('Wake Lock rejected:', err);
  }
  return null;
};

const playMessageReceived = (isMuted) => {
  if (!isMuted) {
    const audio = new Audio('/pop.wav');
    audio.play().catch(e => console.log("Audio play failed (user interaction required first):", e));
  }
};

const playMessageCompleted = (isMuted) => {
  if (!isMuted) {
    const audio = new Audio('/ping.wav');
    audio.play().catch(e => console.log("Audio play failed (user interaction required first):", e));
  }
};

const getModels = async (userTokenValue, SET_MODELS, SET_MODEL_OPTIONS, setLoading) => {
  setLoading(true);
  //const ALLOWED_MODELS = { "mistralai/ministral-3-3b": "Ministral 3 3B", "unsloth/gpt-oss-20b": "GPT-OSS 20B UD" };
  let headers = { 'Content-Type': 'application/json' };
  if (userTokenValue) {
    headers['Authorization'] = `Bearer ${userTokenValue}`;
  }
  const response = await fetch(node_address + 'llmapi/models', {
    method: 'GET',
    headers: headers
  });
  if (response) {
    const models = await response.json();
    SET_MODELS(models);
    SET_MODEL_OPTIONS(Object.keys(models).map(key => { return { key: key, text: models[key]["display"], value: key } }));
  }
  setLoading(false);
}

const parseThinkingContent = (content) => {
  const thinkMatch = content.match(/<(think|thinking)>([\s\S]*?)(?:<\/(?:think|thinking)>|$)/i);   //Matches <think> or <thinking> (case insensitive)
  if (!thinkMatch) { return { isResponding: false, thought: '', answer: content }; }
  const thought = thinkMatch[2];
  const splitContent = content.split(/<\/(?:think|thinking)>/i);
  const answer = splitContent.length > 1 ? splitContent[1].trim() : '';

  return {
    isResponding: true,
    thought: thought,
    answer: answer,
    hasFinishedThinking: splitContent.length > 1 && answer.length > 0
  };
};

const ThoughtBubble = ({ content, thinkingDuration }) => {
  const { isResponding, thought, answer, hasFinishedThinking } = parseThinkingContent(content);
  const [isOpen, setIsOpen] = useState(true); //Open by default so you can see what it is thinking

  useEffect(() => {
    if (hasFinishedThinking) { //once it is done thinking AND it has content after it   // && answer?.trim()
      setIsOpen(false);
    }
  }, [hasFinishedThinking]);

  if (!isResponding) {
    return <ReactMarkdown>{content}</ReactMarkdown>;
  }

  return (
    <div>
      <details
        open={isOpen}
        onToggle={(e) => setIsOpen(e.currentTarget.open)}
        style={{
          marginBottom: '1em',
          borderLeft: '3px solid #e0e0e0',
          paddingLeft: '1em'
        }}
      >
        <summary className="unselectable" style={{ cursor: 'pointer', color: '#898989', fontWeight: 'bold', fontSize: '0.9em' }}>
          {hasFinishedThinking ?
            <span>Thought Process {thinkingDuration ? `(${thinkingDuration.toFixed(1)}s)` : ''}</span>
            :
            <span>Thinking Process...</span>
          }
        </summary>

        <div style={{ marginTop: '0.5em', color: '#676767', fontSize: '0.95em', fontStyle: 'italic' }}>
          <ReactMarkdown>{thought}</ReactMarkdown>
        </div>
      </details>

      {/* The Actual Answer renders below the thought */}
      {answer && (
        <div style={{ marginTop: '1em' }}>
          <ReactMarkdown>{answer}</ReactMarkdown>
        </div>
      )}
    </div>
  );
};

const ChatMessageItem = ({ msg, index, isResponding, loading, onDelete }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); //Resets icon after 2s
  };
  if (msg.isDeleted) {
    return <div key={index}></div>
  }
  return (
    <div key={index}
      style={{
        textAlign: msg.role === 'user' ? 'right' : 'left',
        marginBottom: '1.5em'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {msg?.content ?
        <Message
          compact
          color={msg.role === 'user' ? 'blue' : null}
          style={{
            display: 'inline-block',
            maxWidth: '80%',
            textAlign: 'left',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {/* --- THE ACTION BUTTONS --- */}
          {isHovered &&
            <div
              className="unselectable"
              style={{
                position: 'absolute',
                top: '-2px',
                right: msg.role === 'user' ? 'unset' : '-50px', //Buttons on the left side for user and right side for AI
                left: msg.role === 'user' ? '-50px' : 'unset',
                zIndex: 10,
                display: 'flex',
                gap: '8px',
                //backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '4px',
                padding: '2px 4px',
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
              }}
            >
              {/* Copy Icon */}
              <Icon
                name={copied ? 'check' : 'copy outline'}
                style={{ cursor: 'pointer', margin: 0, color: '#555555' }}
                title="Copy text"
                onClick={handleCopy}
              />
              {/* Delete Icon (Hidden if currently thinking) */}
              {(!isResponding && !loading) ?
                <Icon
                  name='trash alternate outline'
                  style={{ cursor: 'pointer', margin: 0, color: '#D49D9A' }}
                  title="Delete message"
                  onClick={() => onDelete(index)}
                />
                :
                <div style={{ width: "16.5px" }}></div>
              }
            </div>
          }
          <div
            className={`message ${msg.role}`}
            style={{
              position: 'relative',
              marginBottom: '0em'
            }}
          >
            {msg.role === 'system' ? (
              <span style={{ color: 'red' }}>{msg.content}</span>
            ) : (<>
              {msg.role === 'assistant' ?
                <ThoughtBubble content={msg.content} thinkingDuration={msg.thinkingDuration} />
                :
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              }
              {msg.isCancelled && <div style={{ color: 'red', marginTop: '8px', fontWeight: 'bold' }}>
                -- Message Cancelled
              </div>}
            </>
            )}
          </div>
        </Message>
        :
        <></>
      }
    </div>
  );
};

const LLMChatPage = ({ }) => {

  const [loading, setLoading] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [queuePosition, setQueuePosition] = useState(0);

  const [MODELS, SET_MODELS] = useState({});
  const [MODEL_OPTIONS, SET_MODEL_OPTIONS] = useState([]);

  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [modelId, setModelId] = useState('');
  const [modelDisplay, setModelDisplay] = useState('');
  const [modelMaxContext, setModelMaxContext] = useState('');
  const [input, setInput] = useState('');

  const [temperature, setTemperature] = useState(() => {
    const saved = getCookie('temperature_setting');
    return saved ? saved : '0.8';
  });

  const [contextLength, setContextLength] = useState(() => {
    const saved = getCookie('context_length');
    return saved ? saved : '8192';
  });

  const [selectedModel, setSelectedModel] = useState(() => {
    const saved = getCookie('selected_model');
    return saved ? saved : 'mistralai/ministral-3-3b';
  });

  const [isMuted, setIsMuted] = useState(() => {
    const saved = getCookie('app_muted');
    return saved !== null ? saved === 'true' : true;
  });

  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  const [userToken, setUserToken] = useState(() => {
    const saved = getCookie('user_token');
    getModels(saved, SET_MODELS, SET_MODEL_OPTIONS, setLoading); //If saved user_token doesn't have a value it will work fine
    return saved ? saved : null;
  });

  const messagesEndRef = useRef(null);
  const stopMessageRef = useRef(null);
  const userHasInterruptedScroll = useRef(false);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFilesToState(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    addFilesToState(files);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        files.push(items[i].getAsFile());
      }
    }
    if (files.length > 0) addFilesToState(files);
  };

  const processFile = async (file, maxSizeMB, extractor) => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`File too large: ${file.name}. To prevent crashing the AI, files of this type must be under ${maxSizeMB}MB.`);
      return null; //Skip this file
    }
    try {
      const textContent = await extractor(file);
      return { file, type: 'text_file', name: file.name, content: textContent };
    } catch (err) {
      console.error("Could not read file", file.name);
      return null;
    }
  }

  const addFilesToState = async (incomingFiles) => {
    const filesArray = Array.from(incomingFiles);
    let expandedFiles = [];
    for (const file of filesArray) {
      if (file.name.toLowerCase().endsWith('.zip')) {
        console.log(`Extracting ${file.name}...`);
        try {
          const unzippedFiles = await extractZipFiles(file);
          expandedFiles.push(...unzippedFiles); //Push the extracted files
        } catch (err) {
          console.error("Failed to unzip", file.name, err);
          alert(`Could not extract ${file.name}. It might be corrupted or password protected.`);
        }
      } else {
        expandedFiles.push(file); //Not a zip, push the original file
      }
    }
    const newAttachments = await Promise.all(
      expandedFiles.map(async (file) => {
        // If it's an image, create a preview URL and get Base64
        if (file.type.startsWith('image/')) {
          try {
            let finalBase64;

            //console.log(file.size / 1024 / 1024) //prints out the starting image size
            //Compress the image if it's over 10MB, otherwise keep lossless.
            if (file.size > 10 * 1024 * 1024) {
              console.log(`Large image detected. ${(file.size / 1024 / 1024).toFixed(1)}MB. Compressing ${file.name}...`);
              finalBase64 = await compressAndGetBase64(file, 1280, 0.7);
            } else {
              finalBase64 = await fileToBase64(file);
            }

            const payloadByteSize = Math.round((finalBase64.length * 3) / 4); //Calculate the byte size of the resulting Base64 string. Base64 uses 4 characters to represent 3 bytes of data

            //It does the rejection check AFTER compression
            if (payloadByteSize > MAX_IMAGE_SIZE) {
              //`Image file size is too large (${(payloadByteSize / 1024 / 1024).toFixed(1)}MB). Please keep final images under ${MAX_IMAGE_SIZE_MB}MB.`
              const alertMessage = `Image is too large, we even tried compressing it. Compressed size: ${(payloadByteSize / 1024 / 1024).toFixed(1)}MB. Please attach a smaller image than ${MAX_IMAGE_SIZE_MB}MB.`;
              alert(alertMessage);
              return null; //Skip this file
            }

            return {
              file,
              type: 'image',
              name: file.name,
              base64: finalBase64,
              preview: URL.createObjectURL(file)
            };

          } catch (err) {
            console.error("Image processing failed for", file.name, err);
            return null;
          }
        } else if (file.name.toLowerCase().endsWith('.doc')) {
          alert("We don't support .doc files. Please export it as a .docx.");
          return null;
        } else if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
          return await processFile(file, MAX_DOCUMENT_SIZE_MB, xlsxToText);
        } else if (file.name.toLowerCase().endsWith('.docx')) {
          return await processFile(file, MAX_DOCUMENT_SIZE_MB, docxToText);
        } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
          return await processFile(file, MAX_DOCUMENT_SIZE_MB, pdfToText);
        } else { //else if (file.type.startsWith('text/') || file.type === 'application/json' || !file.type || file.name.toLowerCase().endsWith('.csv')) { //it just defaults to attempting to open it as a plain text
          // Defaults to attempting to open it as a plain text
          return await processFile(file, MAX_TEXT_SIZE_MB, fileToText);
        }
      })
    );
    setAttachments((prev) => [...prev, ...newAttachments.filter(Boolean)]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteMessage = (indexToDelete) => {
    setMessages(prev => prev.map((msg, i) => {
      if (i === indexToDelete) {
        return {
          ...msg,
          content: '(The user retracted this message. Please ignore it.)',
          isDeleted: true
        };
      }
      return msg;
    }));
  };

  const handleStop = () => {
    if (stopMessageRef.current) {
      stopMessageRef.current.abort();
      stopMessageRef.current = null;
    }
    setLoading(false);
    setIsResponding(false);
    //setMessages(prev => [...prev, { role: 'system', content: 'Generation stopped by user.' }]);
  };

  const handleSend = async () => {
    if ((!input.trim() && attachments.length === 0) || loading || isResponding) { return };
    let finalInputText = input;

    let messageContent;
    let messageForHistory;


    const textAttachments = attachments.filter(a => (a.type === 'text_file' || a.type === 'document'));
    const imageAttachments = attachments.filter(a => a.type === 'image');

    if (attachments.length > 0) {
      if (textAttachments.length > 0) {
        let appendedFilesText = "\n\n---\n<context>\n";

        textAttachments.forEach(att => {
          appendedFilesText += `\n[Start of Document: ${att.name}]\n`;
          appendedFilesText += `${att.content}\n`;
          appendedFilesText += `[End of Document: ${att.name}]\n`;
        });

        appendedFilesText += "</context>\n";

        // Combine user's typed message with the context
        finalInputText = (input ? input + "\n" : "") + appendedFilesText;
      }

      if (imageAttachments.length > 0) {
        //Array format (Vision API)
        messageContent = [
          { type: "text", text: finalInputText || "What is in these images?" }
        ];

        imageAttachments.forEach(att => {
          messageContent.push({
            type: "image_url",
            image_url: { url: att.base64 }
          });
        });
      } else {
        messageContent = finalInputText;
      }

      const attachmentsLabel = attachments.length === 1 ? " attachment" : " attachments";
      const attachmentHistoryText = attachments.length + attachmentsLabel + ": " + attachments.map(attachment => '“' + attachment.name + '”').join(", ");

      messageForHistory = { role: 'user', content: input + "\n\n" + attachmentHistoryText };
    } else {
      console.log("HERE" + input)
      messageContent = input;
      messageForHistory = { role: 'user', content: input };
    }

    const newMessageObj = {
      role: 'user',
      content: messageForHistory?.content ? messageForHistory.content : messageContent,
      apiContent: messageContent // Hidden field for the LLM
    };

    const updatedHistoryWithAI = [...messages, newMessageObj, { role: 'assistant', content: '' }];
    const payloadMessages = messages.map(msg => {
      if (msg.isCancelled) {
        return { role: msg.role, content: '(This message was retracted. Please ignore it.)' };
      }
      return {
        role: msg.role,
        content: msg.apiContent ? msg.apiContent : msg.content
      };
    });
    payloadMessages.push({ role: 'user', content: messageContent });
    const aiMsgIndex = payloadMessages.length;

    setMessages(updatedHistoryWithAI);
    //setMessages(newHistory);
    setInput('');
    setAttachments([]);
    setLoading(true);
    setIsResponding(true);

    let wakeLock = null;
    stopMessageRef.current = new AbortController();
    try {
      wakeLock = await requestWakeLock();  //For keeping mobile screen on
      const response = await fetch(node_address + 'llmapi/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authorizationtoken: userToken,
          model: selectedModel,
          temperature: temperature,
          context_length: contextLength,
          messages: payloadMessages.map(msg => {
            if (msg.isCancelled) {
              return {
                role: msg.role,
                content: '(This message was retracted. Please ignore it.)'
              };
            }
            return msg;
          }),
        }),
        signal: stopMessageRef.current.signal
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Connection failed");
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let aiText = "";
      let firstTokenReceived = false;
      let startTime; // = Date.now()
      let thinkingEndTime = null;
      let thinkingDuration;

      let streamBuffer = "";

      userHasInterruptedScroll.current = false;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;

        streamBuffer += decoder.decode(value, { stream: true });
        const lines = streamBuffer.split('\n');
        streamBuffer = lines.pop();

        if (!firstTokenReceived && lines.length > 0) {
          setLoading(false);
          //setIsResponding(false); 
          firstTokenReceived = true;
        }

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.replace('data: ', '');
            if (jsonStr.trim() === '[DONE]') { break; }

            try {
              const parsed = JSON.parse(jsonStr);

              if (parsed.type === 'queue') {
                setQueuePosition(parsed.position);
                setLoading(true);
                continue; //Skip the rest, don't add this to chat
              }
              if (parsed.type === 'start') {
                setQueuePosition(0);
                setLoading(true);
                continue;
              }
              if (parsed.choices) {
                const delta = parsed.choices[0].delta.content || "";
                if (aiText || delta) {
                  setLoading(false);
                  if (!aiText) {
                    startTime = Date.now();
                    playMessageReceived(isMuted);
                  }
                }
                aiText += delta;
                if (!thinkingEndTime && ((aiText.includes('</think>') && aiText.includes('<think>')) || (aiText.includes('</thinking>') && aiText.includes('<thinking>')))) {
                  thinkingEndTime = Date.now();
                  thinkingDuration = thinkingEndTime ? (thinkingEndTime - startTime) / 1000 : (Date.now() - startTime) / 1000;
                }
                setMessages(prev => {
                  const newMsgs = [...prev];
                  const latestMsg = newMsgs[newMsgs.length - 1];
                  newMsgs[aiMsgIndex] = {
                    ...latestMsg,
                    role: 'assistant',
                    content: aiText,
                    thinkingDuration: thinkingDuration
                  };
                  return newMsgs;
                });
              }
            } catch (err) {
              console.log(err);
            }
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setMessages(prev => {
          const newMsgs = [...prev];
          const lastIndex = newMsgs.length - 1;
          if (newMsgs[lastIndex].content && newMsgs[lastIndex].role === 'assistant') {
            newMsgs[lastIndex] = {
              ...newMsgs[lastIndex],
              isCancelled: true
            };
          }
          if (lastIndex > 0 && newMsgs[lastIndex - 1].role === 'user') {
            newMsgs[lastIndex - 1] = {
              ...newMsgs[lastIndex - 1],
              isCancelled: true
            };
          }
          return newMsgs;
        });
        return;
      }
      let errorMessage = `Error: ${err.message}`;
      setMessages(prev => {
        const newMsgs = [...prev];
        const lastIndex = newMsgs.length - 1;
        newMsgs[lastIndex] = {
          role: 'system',
          content: errorMessage
        };
        return newMsgs;
      });
    } finally {
      if (wakeLock) { await wakeLock.release(); }
      setLoading(false);
      setIsResponding(false);
      //playMessageCompleted(isMuted);
    }
  };

  const addModel = async () => {
    setLoading(true);

    try {
      const response = await fetch(node_address + 'llmapi/addmodel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authorizationtoken: userToken,
          model_id: modelId,
          model_display: modelDisplay,
          max_context_length: modelMaxContext
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Network response was not ok");
      }
      setModelId("");
      setModelDisplay("");
      setModelMaxContext("");
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;
    const isAtBottom = distanceToBottom < 40;
    if (isAtBottom) {
      userHasInterruptedScroll.current = false;
    } else {
      userHasInterruptedScroll.current = true;
    }
  };


  //Download Raw JSON State
  const handleDownloadJSON = () => {
    const jsonString = JSON.stringify(messages, null, 2);
    downloadFile('chat_history.json', jsonString, 'application/json');
    setShowDownloadMenu(false);
  };

  //Download Plain Text (All Messages)
  const handleDownloadAllText = () => {
    const allText = messages.map(msg => {
      const roleName = msg.role === 'user' ? 'User' : 'Assistant';
      // We use msg.content here because it's the clean string, not the array payload!
      return `${roleName}:\n${msg.content}`;
    }).join('\n\n====================\n\n');

    downloadFile('chat_transcript.txt', allText, 'text/plain');
    setShowDownloadMenu(false);
  };

  //Download Plain Text (Assistant Only, separated by ---)
  const handleDownloadAssistantText = () => {
    const assistantText = messages
      .filter(msg => msg.role === 'assistant' && msg.content.trim())
      .map(msg => msg.content)
      .join('\n\n---\n\n'); // Your requested separator

    downloadFile('ai_responses.txt', assistantText, 'text/plain');
    setShowDownloadMenu(false);
  };

  useEffect(() => {
    setCookie('temperature_setting', temperature, 365);
  }, [temperature]);
  useEffect(() => {
    setCookie('context_length', contextLength, 365);
  }, [contextLength]);
  useEffect(() => {
    setCookie('app_muted', isMuted, 365);
  }, [isMuted]);

  useEffect(() => {
    if (selectedModel) {
      setCookie('selected_model', selectedModel, 365);
      if (MODELS[selectedModel]?.max_context_length && MODELS[selectedModel].max_context_length < contextLength) {
        setContextLength(MODELS[selectedModel].max_context_length);
      }
    }
  }, [selectedModel]);

  useEffect(() => {
    if (!userHasInterruptedScroll.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  useEffect(() => {
    if (userToken === null || userToken === getCookie("user_token")) { return; }
    setCookie('user_token', userToken, 365);
    const timeoutId = setTimeout(() => {
      getModels(userToken, SET_MODELS, SET_MODEL_OPTIONS, setLoading);
    }, 2000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [userToken]);

  return (
    <Grid style={{ margin: 0 }}>
      <Grid.Column align="center" style={{ padding: 0 }}>
        <div>
          <Segment className={`containerSegment`} color='green' raised>
            {/*<Dimmer active={loading} inverted style={{ zIndex: 100 }}>
              <Loader inverted>
                {queuePosition > 0 ?
                  <>
                    <strong>You are in the Queue</strong><br />
                    Position: {queuePosition}<br />
                  </>
                  :
                  <>
                    <strong>Connecting to Mac Mini...</strong><br />
                    <small>Starting generation...</small>
                  </>
                }
              </Loader>
            </Dimmer>*/}
            <h1 style={{ marginBottom: "0px" }}>Chat with my local LLM running off my Mac mini</h1>
            <h3 style={{ marginTop: "0px", color: "red" }}>
              Warning: These chat messages are not private and can be read by me.
              <br />
              DO NOT put in any confidential or sensitive information.
            </h3>
            <div
              style={{ display: 'inline-flex', textAlign: 'left', flexDirection: 'row', flexWrap: 'nowrap', width: '100%' }}
            >
              <div className='unselectable' style={{ display: 'flex', justifyContent: 'space-between', width: '50px', padding: "0px 4px 4px 4px", cursor: 'pointer' }} title={isMuted ? "Unmute sound" : "Mute sound"} onClick={() => setIsMuted(!isMuted)}>
                <Icon
                  name={isMuted ? 'volume off' : 'volume up'}
                  size='large'
                  color={isMuted ? 'grey' : 'black'}
                  style={{ textAlign: 'left' }}
                />
              </div>

              {messages.length > 0 && <div className='unselectable' style={{ position: 'relative', marginLeft: 'auto' }}>
                {/* Main Toggle Button */}
                <button
                  onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                  style={{ padding: '2px 12px', cursor: 'pointer' }}
                >
                  Download Chat
                </button>

                {/* The Download Dropdown Menu */}
                {showDownloadMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: '240px',
                    zIndex: 50,
                    overflow: 'hidden' // Keeps the hover highlight inside the rounded corners
                  }}>
                    <button
                      onClick={handleDownloadJSON}
                      style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #eee', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      JSON
                    </button>
                    <button
                      onClick={handleDownloadAllText}
                      style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #eee', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      All Messages (Plain Text)
                    </button>
                    <button
                      onClick={handleDownloadAssistantText}
                      style={{ padding: '10px', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      Bot Messages Only (Plain Text)
                    </button>
                  </div>
                )}
              </div>}
            </div>
            <Segment
              attached
              style={{ height: '60vh', overflowY: 'auto', background: '#EDEAEC' }}
              onScroll={handleScroll}
            >
              {messages.length === 0 && (
                <Message info>
                  <Message.Header>Welcome!</Message.Header>
                  <p>Select a model and start typing to queue your request.</p>
                  <h4 style={{ marginTop: "0px", color: "red" }}>
                    These chat messages are not private and can be read by me.
                    <br />
                    DO NOT put in any confidential or sensitive information.
                  </h4>
                </Message>
              )}
              {messages.map((msg, index) => (
                <ChatMessageItem
                  key={index}
                  index={index}
                  msg={msg}
                  isResponding={isResponding}
                  loading={loading}
                  onDelete={handleDeleteMessage}
                />
              ))}
              {(loading && isResponding && messages.length > 0 && !messages.length[messages.length - 1]?.content && !messages.length[messages.length - 1]?.role) &&
                <div
                  style={{
                    textAlign: 'left',
                    marginBottom: '1.5em'
                  }}>
                  <Message
                    info
                    icon
                    compact
                    color={null}
                    style={{
                      display: 'inline-block',
                      minWidth: '220px',
                      width: '30%',
                      paddingLeft: "12px",
                      textAlign: 'left',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "2px"
                    }}>

                      {queuePosition > 0 ?
                        <>
                          <style>
                            {`
                                @keyframes hourglassFlip {
                                  20% { transform: rotate(0deg); }
                                  50% { transform: rotate(180deg); }
                                  100% { transform: rotate(180deg); }
                                }
                              `}
                          </style>
                          <Icon
                            name='hourglass half'
                            style={{
                              animation: 'hourglassFlip 2s infinite ease-in-out',
                              fontSize: '20px',
                              marginTop: '4px'
                            }}
                          />
                          <div>
                            <Message.Header>You are in the Queue</Message.Header>
                            Position: {queuePosition}<br />
                          </div></>
                        :
                        <>
                          <Icon name='circle notched' style={{ fontSize: '16px' }} loading />
                          <Message.Header>Generating message...</Message.Header>
                        </>
                      }
                    </div>
                  </Message></div>
              }
              <div ref={messagesEndRef} />
            </Segment>
            <div style={{ textAlign: "left", marginTop: "8px" }}>
              <Form style={{ marginBottom: "6px" }}>
                <Form.Group style={{ margin: "0px 0px 4px 0px", display: "flex", flexWrap: "nowrap" }}>
                  <div
                    onDrop={handleDrop}
                    onPaste={handlePaste}
                    onDragOver={(e) => e.preventDefault()} // Required to allow dropping
                    style={{ border: '0px solid #ccc', borderRadius: '8px', padding: '0px', position: 'relative', width: "100%" }}
                  >
                    <TextArea
                      id="inputtextarea"
                      placeholder={userToken ? "Type your prompt here... (Shift+Enter for new line)\nOr drag & drop files here." : "Please put in your API Token below."}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      rows={1}
                      style={{ padding: "6px", minHeight: 72, resize: 'none' }}
                      disabled={!userToken}
                    />
                    <div style={{ pointerEvents: 'none', position: 'absolute', bottom: '10px', right: '10px' }}>
                      {(MODELS[selectedModel]?.vision) && <span inert className="unselectable" style={{ fontSize: '14px', color: '#89898955' }}>Vision </span>}
                      <label style={{ pointerEvents: 'auto', cursor: 'pointer', fontSize: '32px', color: '#89898977' }} title="Attach file">
                        +
                        <input
                          type="file"
                          multiple
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        //accept="image/*,.txt,.pdf" // Define what you allow
                        />
                      </label>
                    </div>
                    {/* ATTACHMENT PREVIEWS */}
                    {attachments.length > 0 && (
                      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                        {attachments.map((att, i) => (
                          <div key={i} style={{ position: 'relative', border: '1px solid #ddd', padding: '4px', borderRadius: '4px' }}>
                            {att.type === 'image' ? (
                              <img src={att.preview} alt="preview" style={{ height: '50px', width: '50px', objectFit: 'cover' }} />
                            ) : (
                              <div style={{ height: '50px', width: '50px', display: 'flex', alignItems: 'center', fontSize: '10px', overflow: 'hidden' }}>
                                📄 {att.name}
                              </div>
                            )}
                            {/* Delete Attachment Button */}
                            <button
                              onClick={() => removeAttachment(i)}
                              style={{ position: 'absolute', top: '-5px', right: '-5px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer' }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    style={{
                      width: "80px",
                      border: "1px solid #999999",
                      color: ((loading || (!input.trim() && attachments.length === 0)) && !isResponding) ? "#BBBBBB" : "#222222",
                      backgroundColor: isResponding ? "#EE5A49" : (loading || (!input.trim() && attachments.length === 0)) ? "#99AA77" : "#AAEE99"
                    }}
                    onClick={isResponding ? handleStop : handleSend}
                    icon={"true"}
                    loading={loading ? 'true' : ''}
                    disabled={!isResponding && !input.trim() && attachments.length === 0}
                  >{isResponding ?
                    <span style={{ fontSize: "32px", lineHeight: "0", padding: "10.5px", display: "inline-block", transform: "translateY(-4px)" }}>■</span>
                    :
                    <>Send <Icon name='paper plane' /></>
                    }
                  </Button>
                </Form.Group>
              </Form>
              <select name="model" id="model" value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)} disabled={loading}
              >
                {MODEL_OPTIONS.map((model, index) => { return <option key={index} value={model.key}>{model.text}</option> })}
              </select> <input
                type="text"
                style={{ marginLeft: "4px", width: "40px" }}
                value={temperature}
                onChange={(e) => setTemperature(e.target.value)}
              />
              <input
                type="text"
                style={{ marginLeft: "4px", width: "60px" }}
                value={contextLength}
                onChange={(e) => setContextLength(e.target.value)}
              /> (Context Length, allowed model max: {MODELS[selectedModel]?.max_context_length || "8192"})
              <br />
              <br />
              <TextField
                type="password"
                //variant="outlined"
                style={{ width: "300px", border: "1px solid #AAAAAA", backgroundColor: "#FFFFFF", paddingLeft: "4px" }}
                placeholder="Your API Token Here"
                value={userToken || ''}
                onChange={(e) => setUserToken(e.target.value)}
              />
              {userToken?.substring(0, 5) === "admin" && <div style={{ margin: "16px 0px" }}>

                <h3 style={{ marginBottom: "8px" }}>Admin Panel</h3>
                <TextField
                  type="text"
                  //variant="outlined"
                  style={{ width: "260px", border: "1px solid #AAAAAA", backgroundColor: "#FFFFFF", paddingLeft: "4px" }}
                  placeholder="Add Model (Key ID)"
                  value={modelId}
                  onChange={(e) => setModelId(e.target.value)}
                />  <TextField
                  type="text"
                  //variant="outlined"
                  style={{ width: "260px", border: "1px solid #AAAAAA", backgroundColor: "#FFFFFF", paddingLeft: "4px" }}
                  placeholder="Add Model (Display Name)"
                  value={modelDisplay}
                  onChange={(e) => setModelDisplay(e.target.value)}
                />  <TextField
                  type="text"
                  //variant="outlined"
                  style={{ width: "260px", border: "1px solid #AAAAAA", backgroundColor: "#FFFFFF", paddingLeft: "4px" }}
                  placeholder="Add Model (Max Context, 8192 default)"
                  value={modelMaxContext}
                  onChange={(e) => setModelMaxContext(e.target.value)}
                /> <button onClick={addModel} disabled={!modelId || !modelDisplay}>Add Model To List</button>
              </div>}
            </div>
          </Segment>
          <br className={`unselectable`} />
          <div className={`bottomSegment`}>
            {/*<Link to="/"><Segment className={`bottomSegment unselectable`} inverted raised onClick={() => { }}>
                <h2>Back</h2>
              </Segment></Link>*/}
          </div>
        </div>
      </Grid.Column>
    </Grid>
  );
};

export default LLMChatPage;